import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, RefreshCw } from "lucide-react";
import {
  listCatalogCountryCoverage,
  type CountryCoverageRow,
} from "@/lib/catalog-map-audit.functions";
import {
  getTemplateInfo,
  type PublicationGate,
} from "@/lib/catalog-country-templates";

export const Route = createFileRoute("/pokryti-katalogu")({
  ssr: false,
  component: CoveragePage,
  head: () => {
    const title = "Pokrytí katalogu — Cerveza International";
    const description =
      "Přehled stavu importu a publikace pivovarských provozoven po zemích: verze šablony (V1/V2), počty sites, GPS pokrytí a Publication Gate status.";
    const url = "https://cerveza.international/pokryti-katalogu";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

type StatusFilter = "all" | "published" | "imported" | "not_imported";

function gateBadgeVariant(g: PublicationGate) {
  switch (g) {
    case "PASS":
      return "default" as const;
    case "PARTIAL":
      return "secondary" as const;
    case "PENDING":
      return "outline" as const;
    default:
      return "outline" as const;
  }
}

function gateBadgeClass(g: PublicationGate) {
  switch (g) {
    case "PASS":
      return "bg-emerald-600 hover:bg-emerald-600 text-white";
    case "PARTIAL":
      return "bg-amber-500 hover:bg-amber-500 text-white";
    case "PENDING":
      return "border-amber-500 text-amber-700";
    default:
      return "border-muted-foreground/40 text-muted-foreground";
  }
}

function statusLabel(s: CountryCoverageRow["status"]) {
  if (s === "PUBLISHED") return "Publikováno";
  if (s === "IMPORTED_NOT_PUBLISHED") return "Importováno, nepublikováno";
  return "Neimportováno";
}

function statusBadgeClass(s: CountryCoverageRow["status"]) {
  if (s === "PUBLISHED") return "bg-emerald-600 hover:bg-emerald-600 text-white";
  if (s === "IMPORTED_NOT_PUBLISHED")
    return "bg-amber-500 hover:bg-amber-500 text-white";
  return "bg-muted text-muted-foreground";
}

function CoveragePage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["catalog-country-coverage"],
    queryFn: () => listCatalogCountryCoverage(),
    staleTime: 60_000,
  });

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");

  const rows = useMemo(() => {
    const list = (data ?? []).map((r) => ({
      ...r,
      tpl: getTemplateInfo(r.iso2),
    }));
    const qNorm = q.trim().toLowerCase();
    return list.filter((r) => {
      if (filter === "published" && r.status !== "PUBLISHED") return false;
      if (filter === "imported" && r.status !== "IMPORTED_NOT_PUBLISHED")
        return false;
      if (filter === "not_imported" && r.status !== "NOT_IMPORTED") return false;
      if (!qNorm) return true;
      return (
        r.name_en.toLowerCase().includes(qNorm) ||
        r.iso2.toLowerCase().includes(qNorm)
      );
    });
  }, [data, q, filter]);

  const totals = useMemo(() => {
    const list = data ?? [];
    return {
      countries: list.length,
      published: list.filter((r) => r.status === "PUBLISHED").length,
      imported: list.filter((r) => r.status === "IMPORTED_NOT_PUBLISHED").length,
      not_imported: list.filter((r) => r.status === "NOT_IMPORTED").length,
      sites_published_gps: list.reduce(
        (a, r) => a + r.sites_published_with_gps,
        0,
      ),
    };
  }, [data]);

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" /> Pokrytí katalogu podle zemí
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Stav importu a publikace pivovarských provozoven. Verze šablony a
            Publication Gate jsou vedené z Cerveza audit workbooků.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/mapa">
            <Button variant="outline" size="sm">
              <MapPin className="h-4 w-4 mr-1" /> Mapa
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Zemí v katalogu" value={totals.countries} />
        <StatCard label="Publikováno" value={totals.published} tone="ok" />
        <StatCard label="Importováno" value={totals.imported} tone="warn" />
        <StatCard label="Neimportováno" value={totals.not_imported} tone="muted" />
        <StatCard label="Sites s GPS" value={totals.sites_published_gps} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input
          placeholder="Hledat zemi nebo ISO-2…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        {(
          [
            ["all", "Vše"],
            ["published", "Publikováno"],
            ["imported", "Importováno"],
            ["not_imported", "Neimportováno"],
          ] as [StatusFilter, string][]
        ).map(([id, label]) => (
          <Button
            key={id}
            size="sm"
            variant={filter === id ? "default" : "outline"}
            onClick={() => setFilter(id)}
          >
            {label}
          </Button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Načítám…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Žádná země neodpovídá filtru.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Země</th>
                  <th className="px-3 py-2">ISO</th>
                  <th className="px-3 py-2">Šablona</th>
                  <th className="px-3 py-2">Gate</th>
                  <th className="px-3 py-2 text-right">Sites celkem</th>
                  <th className="px-3 py-2 text-right">Publikováno</th>
                  <th className="px-3 py-2 text-right">S GPS</th>
                  <th className="px-3 py-2 text-right">Venues (GPS)</th>
                  <th className="px-3 py-2">Stav</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.iso2 || r.name_en} className="border-t">
                    <td className="px-3 py-2 font-medium">{r.name_en}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.iso2 || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.tpl.template_version ? (
                        <Badge variant="outline" className="font-mono">
                          {r.tpl.template_version}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={gateBadgeVariant(r.tpl.gate)}
                        className={gateBadgeClass(r.tpl.gate)}
                      >
                        {r.tpl.gate}
                      </Badge>
                      {r.tpl.notes && (
                        <div
                          className="text-[11px] text-muted-foreground mt-1 max-w-[240px]"
                          title={r.tpl.notes}
                        >
                          {r.tpl.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.sites_total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.sites_published}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.sites_published_with_gps}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.venues_published_with_gps}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={statusBadgeClass(r.status)}>
                        {statusLabel(r.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-6 text-xs text-muted-foreground space-y-1">
        <p>
          <strong>Šablona:</strong> V1 = základní 20-sheet audit, V2 = rozšířený
          set (kompletní craft, licensing, SKU/EAN).
        </p>
        <p>
          <strong>Gate:</strong> PASS = připraveno k publikaci, PARTIAL = běží
          doplňování, PENDING = čeká na V2 rozšíření, NOT_STARTED = bez
          šablony.
        </p>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ok" | "warn" | "muted";
}) {
  const color =
    tone === "ok"
      ? "text-emerald-600"
      : tone === "warn"
      ? "text-amber-600"
      : tone === "muted"
      ? "text-muted-foreground"
      : "text-foreground";
  return (
    <Card className="p-3">
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
    </Card>
  );
}
